if (!customElements.get("media-gallery")) {
  customElements.define(
    "media-gallery",
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };

        this.mql = window.matchMedia("(min-width: 750px)");

        /*
         * GALLERY SLIDER EVENTS
         */
        if (this.elements.viewer) {
          this.elements.viewer.addEventListener(
            "slideChanged",
            debounce(this.onSlideChanged.bind(this), 500),
          );
        }

        /*
         * THUMBNAIL BUTTONS
         */
        if (this.elements.thumbnails) {
          this.elements.thumbnails
            .querySelectorAll("[data-target]")
            .forEach((mediaToSwitch) => {
              const button = mediaToSwitch.querySelector("button");

              if (button) {
                button.addEventListener(
                  "click",
                  this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false),
                );
              }
            });
        }

        /*
         * DESKTOP THUMBNAIL LAYOUT
         */
        if (
          this.elements.thumbnails &&
          this.dataset.desktopLayout &&
          this.dataset.desktopLayout.includes("thumbnail") &&
          this.mql.matches
        ) {
          this.removeListSemantic();
        }
      }

      /*
       * =====================================================
       * LIFECYCLE
       * =====================================================
       */
      connectedCallback() {
        /*
         * Fallback: listen for any option change inside this
         * product's variant picker (selects, radios, custom "size").
         */
        this.variantChangeHandler = (event) => {
          const target = event.target;
          const scope = this.getProductScope();

          if (!scope.contains(target)) return;

          const isVariantInput =
            target.matches('input[name="size"]') ||
            target.closest("variant-selects, variant-radios");

          if (!isVariantInput) return;

          // Let the theme finish updating its own state first.
          setTimeout(() => this.updateGalleryFromVariant(), 50);
        };

        document.addEventListener("change", this.variantChangeHandler);

        /*
         * Primary: Dawn publishes variantChange AFTER it has
         * re-rendered / re-ordered the gallery, so filtering
         * here can't be overwritten by the theme.
         */
        if (
          typeof subscribe === "function" &&
          typeof PUB_SUB_EVENTS !== "undefined" &&
          PUB_SUB_EVENTS.variantChange
        ) {
          this.variantChangeUnsubscriber = subscribe(
            PUB_SUB_EVENTS.variantChange,
            (event) => {
              const data = event && event.data;
              if (!data || !data.variant) return;
              if (this.dataset.section && data.sectionId && data.sectionId !== this.dataset.section) return;

              const size = (data.variant.options || []).find((option) =>
                this.getSize(option),
              );

              if (size) this.filterGalleryByVariant(size);
            },
          );
        }

        /*
         * Run on page load.
         */
        setTimeout(() => this.updateGalleryFromVariant(), 300);
      }

      disconnectedCallback() {
        document.removeEventListener("change", this.variantChangeHandler);

        if (typeof this.variantChangeUnsubscriber === "function") {
          this.variantChangeUnsubscriber();
        }
      }

      /*
       * =====================================================
       * SLIDE CHANGED
       * =====================================================
       */
      onSlideChanged(event) {
        if (!this.elements.thumbnails) return;

        const currentElement = event.detail.currentElement;
        if (!currentElement) return;

        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${currentElement.dataset.mediaId}"]`,
        );

        this.setActiveThumbnail(thumbnail);
      }

      /*
       * =====================================================
       * SET ACTIVE MEDIA
       * =====================================================
       */
      setActiveMedia(mediaId, prepend) {
        if (!this.elements.viewer) return;

        const activeMedia = this.elements.viewer.querySelector(
          `[data-media-id="${mediaId}"]`,
        );

        if (!activeMedia) return;

        this.elements.viewer
          .querySelectorAll("[data-media-id]")
          .forEach((element) => element.classList.remove("is-active"));

        activeMedia.classList.add("is-active");

        if (prepend) {
          activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(
              `[data-target="${mediaId}"]`,
            );

            if (activeThumbnail) {
              activeThumbnail.parentElement.prepend(activeThumbnail);
            }
          }

          if (this.elements.viewer.slider) {
            this.elements.viewer.resetPages();
          }
        }

        this.preventStickyHeader();

        window.setTimeout(() => {
          if (this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }

          if (!this.elements.thumbnails || this.dataset.desktopLayout === "stacked") {
            activeMedia.scrollIntoView({ behavior: "smooth" });
          }
        });

        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;

        const activeThumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${mediaId}"]`,
        );

        if (activeThumbnail) {
          this.setActiveThumbnail(activeThumbnail);
          this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
        }
      }

      /*
       * =====================================================
       * HELPERS
       * =====================================================
       */

      /*
       * Product section that owns this gallery.
       */
      getProductScope() {
        return (
          this.closest("product-info, .shopify-section, section") || document
        );
      }

      /*
       * Extract a size from text and return it as a number string.
       *
       * "100ml" -> "100"   "100 ML" -> "100"   "300-ml" -> "300"
       * "Bottle 300ml front" -> "300"   "Lifestyle" -> null
       */
      getSize(value) {
        const match = (value || "")
          .toString()
          .match(/(\d+(?:\.\d+)?)[\s_-]*ml\b/i);

        return match ? String(parseFloat(match[1])) : null;
      }

      /*
       * =====================================================
       * FILTER GALLERY BY VARIANT
       * =====================================================
       *
       * Shows only media whose ALT text contains the selected
       * size (e.g. "100ml"). Everything else is hidden.
       */
      filterGalleryByVariant(variantValue) {
        if (!this.elements.viewer) return;

        const selectedSize = this.getSize(variantValue);
        if (!selectedSize) return;

        const mediaItems = Array.from(
          this.elements.viewer.querySelectorAll("[data-media-id]"),
        );

        const thumbnailItems = this.elements.thumbnails
          ? Array.from(this.elements.thumbnails.querySelectorAll("[data-target]"))
          : [];

        const matchesSize = (element) =>
          this.getSize(element.dataset.mediaAlt) === selectedSize;

        const matchingMedia = mediaItems.filter(matchesSize);

        /*
         * No image tagged for this size: show everything
         * rather than an empty gallery.
         */
        if (matchingMedia.length === 0) {
          mediaItems.forEach((media) => media.classList.remove("variant-hidden"));
          thumbnailItems.forEach((thumb) => thumb.classList.remove("variant-hidden"));
          this.refreshSliders(mediaItems.length);
          return;
        }

        mediaItems.forEach((media) => {
          media.classList.toggle("variant-hidden", !matchesSize(media));
        });

        thumbnailItems.forEach((thumbnail) => {
          thumbnail.classList.toggle("variant-hidden", !matchesSize(thumbnail));
        });

        /*
         * Only switch the active image if the current one is now
         * hidden — avoids unnecessary scroll jumps.
         */
        const currentActive = this.elements.viewer.querySelector(
          "[data-media-id].is-active",
        );

        if (!currentActive || currentActive.classList.contains("variant-hidden")) {
          this.setActiveMedia(matchingMedia[0].dataset.mediaId, false);
        }

        this.refreshSliders(matchingMedia.length);
      }

      /*
       * Recalculate slider pages + counter after show/hide.
       */
      refreshSliders(visibleCount) {
        if (
          this.elements.viewer.slider &&
          typeof this.elements.viewer.resetPages === "function"
        ) {
          this.elements.viewer.resetPages();
        }

        if (
          this.elements.thumbnails &&
          this.elements.thumbnails.slider &&
          typeof this.elements.thumbnails.resetPages === "function"
        ) {
          this.elements.thumbnails.resetPages();
        }

        const totalCounter = this.querySelector(".slider-counter--total");
        if (totalCounter) totalCounter.textContent = visibleCount;

        const currentCounter = this.querySelector(".slider-counter--current");
        if (currentCounter) currentCounter.textContent = "1";
      }

      /*
       * =====================================================
       * READ SELECTED VARIANT FROM THE DOM
       * =====================================================
       */
      updateGalleryFromVariant() {
        const scope = this.getProductScope();

        /*
         * Custom size radio buttons.
         */
        const customSize = scope.querySelector('input[name="size"]:checked');

        if (customSize && this.getSize(customSize.value)) {
          this.filterGalleryByVariant(customSize.value);
          return;
        }

        /*
         * Shopify variant picker (dropdowns and/or pills).
         */
        const variantPicker = scope.querySelector("variant-selects, variant-radios");
        if (!variantPicker) return;

        const selectedValues = [
          ...Array.from(variantPicker.querySelectorAll("select")).map(
            (select) => select.value,
          ),
          ...Array.from(
            variantPicker.querySelectorAll('input[type="radio"]:checked'),
          ).map((input) => input.value),
        ];

        const sizeValue = selectedValues.find((value) => this.getSize(value));

        if (sizeValue) {
          this.filterGalleryByVariant(sizeValue);
        }
      }

      /*
       * =====================================================
       * SET ACTIVE THUMBNAIL
       * =====================================================
       */
      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll("button")
          .forEach((element) => element.removeAttribute("aria-current"));

        const button = thumbnail.querySelector("button");
        if (button) button.setAttribute("aria-current", true);

        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        if (this.elements.thumbnails.slider) {
          this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
        }
      }

      /*
       * =====================================================
       * ANNOUNCE LIVE REGION
       * =====================================================
       */
      announceLiveRegion(activeItem, position) {
        if (!this.elements.liveRegion) return;

        const image = activeItem.querySelector(".product__modal-opener--image img");
        if (!image) return;

        image.onload = () => {
          this.elements.liveRegion.setAttribute("aria-hidden", false);
          this.elements.liveRegion.innerHTML =
            window.accessibilityStrings.imageAvailable.replace("[index]", position);

          setTimeout(() => {
            this.elements.liveRegion.setAttribute("aria-hidden", true);
          }, 2000);
        };

        image.src = image.src;
      }

      /*
       * =====================================================
       * PLAY ACTIVE MEDIA
       * =====================================================
       */
      playActiveMedia(activeItem) {
        window.pauseAllMedia();

        const deferredMedia = activeItem.querySelector(".deferred-media");
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      /*
       * =====================================================
       * PREVENT STICKY HEADER
       * =====================================================
       */
      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector("sticky-header");
        if (!this.stickyHeader) return;

        this.stickyHeader.dispatchEvent(new Event("preventHeaderReveal"));
      }

      /*
       * =====================================================
       * REMOVE LIST SEMANTIC
       * =====================================================
       */
      removeListSemantic() {
        if (!this.elements.viewer || !this.elements.viewer.slider) return;

        this.elements.viewer.slider.setAttribute("role", "presentation");
        this.elements.viewer.sliderItems.forEach((slide) =>
          slide.setAttribute("role", "presentation"),
        );
      }
    },
  );
}
